import { type SyncedUserSetting, type UserSetting } from '@soulhx/fs-common';
import { DateTime } from 'luxon';
import { db, type ItemSyncStatus, type LocalUserSetting } from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { syncFetch } from './syncFetch.js';
import { pullSince } from './timestamps.js';

export const SETTING_SYNC_POLL_INTERVAL_MS = 20_000;
export const SETTINGS_SYNC_KEY = 'settings';

const SETTINGS_PATH = '/auth/u/s';

// Earliest possible cursor, used on the very first pull. Passing `since` at
// all (even this far back) routes the server to the sync-oriented delta
// endpoint - every setting the user has ever customized, each with a
// `lastUpdate` - rather than the default-filled snapshot it returns when
// `since` is omitted entirely, which has no `lastUpdate` to build a cursor
// from and would fill local storage with settings the user never touched.
const EPOCH = new Date(0).toISOString();

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

export const pullSettings = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(SETTINGS_SYNC_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const serverSettings = await syncFetch<SyncedUserSetting[]>({
    method: 'GET',
    path: `${SETTINGS_PATH}?since=${encodeURIComponent(pullSince(since))}`,
  });

  let maxLastUpdate = since;

  await db.transaction('rw', db.settings, db.syncMeta, async () => {
    for (const serverSetting of serverSettings) {
      const { settingId, settingStringValue, settingBoolValue, lastUpdate, deleted } = serverSetting;

      if (lastUpdate > maxLastUpdate) {
        maxLastUpdate = lastUpdate;
      }

      // unlike other synced rows, a setting's key is fixed rather than a
      // fresh uuid, so a tombstone re-pulled by the cursor overlap (see
      // pullSince) can name a setting the user has since changed again -
      // a pending local edit wins here just as it does over a put
      const local = await db.settings.get(settingId);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (deleted) {
        if (local) await db.settings.delete(settingId);
        continue;
      }

      if (local?.lastUpdate === lastUpdate) continue;

      const toStore: LocalUserSetting = {
        settingId,
        settingStringValue,
        settingBoolValue,
        lastUpdate,
        syncStatus: 'synced',
      };
      await db.settings.put(toStore);
    }

    await db.syncMeta.put({ itemId: SETTINGS_SYNC_KEY, lastSyncedAt: maxLastUpdate });
  });
};

export const pushPendingSettings = async (options?: { keepalive?: boolean }): Promise<void> => {
  const pending = await db.settings.filter((s) => isUnsynced(s.syncStatus)).toArray();
  if (pending.length === 0) return;

  try {
    const body: UserSetting[] = pending.map(({ settingId, settingStringValue, settingBoolValue }) => ({
      settingId,
      settingStringValue,
      settingBoolValue,
    }));
    const saved = await syncFetch<SyncedUserSetting[]>({
      method: 'PUT',
      path: `${SETTINGS_PATH}/b`,
      body,
      keepalive: options?.keepalive,
    });

    await db.transaction('rw', db.settings, async () => {
      for (const { settingId, settingStringValue, settingBoolValue, lastUpdate } of saved) {
        const toStore: LocalUserSetting = {
          settingId,
          settingStringValue,
          settingBoolValue,
          lastUpdate,
          syncStatus: 'synced',
        };
        await db.settings.put(toStore);
      }
    });
  } catch (error) {
    // leave the rows' syncStatus untouched so they're retried on the next cycle
    // eslint-disable-next-line no-console
    console.error('setting sync: failed to push pending settings', error);
  }
};

/**
 * Pushes any pending local setting changes, then pulls whatever changed on
 * the server since the last sync. Push-before-pull so local changes reach
 * `synced` before we ask "what's new", minimizing cases where the pull
 * side has to skip an in-flight local row.
 */
export const syncSettings = async (): Promise<void> => {
  await pushPendingSettings();
  await pullSettings();
};

export const queueLocalSettingUpdate = async (setting: UserSetting): Promise<void> => {
  const toStore: LocalUserSetting = {
    ...setting,
    lastUpdate: DateTime.now().toISO(),
    syncStatus: 'pendingUpdate',
  };

  await db.settings.put(toStore);
  triggerGlobalSync(SETTINGS_SYNC_KEY);
};
